import React, { useEffect, useMemo } from 'react';
import { BsArrowCounterclockwise, BsBoxArrowUpRight, BsExclamationTriangleFill, BsFileEarmarkPdf, BsInfoCircle, BsPencilSquare } from 'react-icons/bs';
import { Box, Button, Flex, HStack, Icon, Link, Text, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import colors from '../../../../../../../../../../../../../../../styles/theme/foundation/colors';

const ExtractionFailedModal = ({
  file,
  onUploadWithoutDate,
  onTryAgain,
  onEnterManually,
  isRetrying = false
}) => {
  const fileUrl = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => () => URL.revokeObjectURL(fileUrl), [fileUrl]);

  return (
    <Box textAlign="left">
      <Flex alignItems="flex-start" gap={5}>
        <Flex
          alignItems="center"
          bg="#FDF3D8"
          borderRadius="full"
          boxSize="72px"
          flexShrink={0}
          justifyContent="center"
        >
          <Icon as={BsExclamationTriangleFill} boxSize="30px" color="#A9781F" />
        </Flex>

        <Box>
          <Text
            color={colors.greyScale['900']}
            fontSize="22px"
            fontWeight="semibold"
            lineHeight="30px"
            mb={2}
          >
            We couldn&apos;t extract an expiration date
          </Text>

          <Text color={colors.greyScale['700']} fontSize="17px" lineHeight="26px">
            The scan failed or timed out. Add the date manually, or upload without one and add it later.
          </Text>

          <Link
            isExternal
            alignItems="center"
            color={colors.primaryScale['700']}
            display="inline-flex"
            fontSize="17px"
            fontWeight="semibold"
            gap={2}
            href={fileUrl}
            mt={3}
          >
            <Icon as={BsFileEarmarkPdf} boxSize="18px" />
            {file.name}
            <Icon as={BsBoxArrowUpRight} boxSize="14px" />
          </Link>
        </Box>
      </Flex>

      <Flex
        alignItems="center"
        bg={colors.backgroundScale['50']}
        borderRadius="lg"
        color={colors.primaryScale['800']}
        fontSize="17px"
        gap={4}
        mt={8}
        px={6}
        py={5}
      >
        <Icon as={BsInfoCircle} boxSize="20px" flexShrink={0} />
        <Text>The document uploads either way &mdash; only the expiration date is missing.</Text>
      </Flex>

      <VStack mt={8} spacing={4}>
        <HStack flexWrap="wrap" justifyContent="center" spacing={4} w="100%">
          <Button
            isDisabled={isRetrying}
            minW="220px"
            size="lg"
            variant="secondary"
            onClick={onUploadWithoutDate}
          >
            Upload Without Date
          </Button>

          <Button
            isLoading={isRetrying}
            leftIcon={<Icon as={BsArrowCounterclockwise} boxSize="18px" />}
            minW="220px"
            size="lg"
            variant="secondary"
            onClick={onTryAgain}
          >
            Try Again
          </Button>
        </HStack>

        <Button
          isDisabled={isRetrying}
          leftIcon={<Icon as={BsPencilSquare} boxSize="18px" />}
          minW="320px"
          size="lg"
          variant="primary"
          onClick={onEnterManually}
        >
          Enter Date Manually
        </Button>
      </VStack>
    </Box>
  );
};

ExtractionFailedModal.propTypes = {
  file: PropTypes.object.isRequired,
  onUploadWithoutDate: PropTypes.func.isRequired,
  onTryAgain: PropTypes.func.isRequired,
  onEnterManually: PropTypes.func.isRequired,
  isRetrying: PropTypes.bool
};

export default ExtractionFailedModal;
